import { useEffect, useState, type MouseEvent } from 'react';
import type { AuthState } from '@jarvis/shared';
import { api } from '../lib/api';
import { authStore, useAuth } from '../lib/auth';
import { APP_VERSION } from '@jarvis/shared/constants';
import { Icon, type IconName } from '../components/Icon';
import { toast } from '../components/toast';
import { CardHead, PageHead } from '../components/ui';
import { fmt } from '../lib/format';
import { prefsStore, usePrefs, type StateMode, type Theme } from '../lib/prefs';
import { replaceHash } from '../lib/router';
import { ActivityList, DataControls, DeleteAccount, TimezoneField } from './SettingsLive';
import { AiProviderStatus, GmailIntegration, LimitsLive, MemoryManager, VoicePicker, WebSearchStatus } from './SettingsAi';

/* Estado real das integrações. Não é demonstração: nada está conectado. */
const INTEGRATIONS: { name: string; icon: IconName; scope: string; stage: string | null }[] = [
  { name: 'Gmail', icon: 'mail', scope: 'Leitura de mensagens; rascunhos e envio em etapas posteriores', stage: '4' },
  { name: 'Google Agenda', icon: 'calendar', scope: 'Leitura de eventos; criação só com confirmação', stage: '5' },
  { name: 'Feeds de notícias (RSS)', icon: 'rss', scope: 'Fontes que você escolher', stage: '3' },
  { name: 'Outlook / Microsoft 365', icon: 'mail', scope: 'Não prioritário: você escolheu Google. Pode entrar depois, se precisar', stage: null },
  { name: 'Telefone e mensagens', icon: 'phone', scope: 'Sem data definida; exige avaliação de segurança própria', stage: null },
  { name: 'Serviços financeiros', icon: 'chart', scope: 'Fora do escopo atual; apenas notícias com fonte', stage: null }
];

const PROVIDERS: { name: string; icon: IconName; note: string }[] = [
  { name: 'Reconhecimento de voz', icon: 'mic', note: 'Voz do navegador ou serviço de transcrição; escolha considera privacidade e custo. Etapa 6.' },
  { name: 'Síntese de voz', icon: 'monitor', note: 'Voz do próprio navegador, escolhida na seção Voz. Uma voz premium por serviço externo é opcional, mais adiante.' }
];

const SECTIONS: [string, string][] = [
  ['perfil', 'Perfil'], ['aparencia', 'Aparência'], ['voz', 'Voz'], ['integracoes', 'Contas e integrações'],
  ['provedores', 'Provedores de IA'], ['limites', 'Limites e custos'], ['memoria', 'Memória'],
  ['privacidade', 'Privacidade e dados'], ['laboratorio', 'Laboratório de estados'], ['sobre', 'Sobre']
];

const stageLabel = (s: string | null) => (s ? `Etapa ${s}` : 'Sem etapa definida');

function RadioCards<T extends string>({ name, value, options, onChange }: {
  name: string; value: T; options: { value: T; label: string; hint: string }[]; onChange: (v: T) => void;
}) {
  return (
    <div className="radio-cards" role="radiogroup">
      {options.map((o) => (
        <label key={o.value} className="radio-card">
          <input type="radio" name={name} value={o.value} checked={o.value === value} onChange={() => onChange(o.value)} />
          <b>{o.label}</b><span>{o.hint}</span>
        </label>
      ))}
    </div>
  );
}

/* Rola só a janela (scrollIntoView também moveria contêineres, inclusive a barra lateral fixa). */
const TOPBAR_OFFSET = 84;
function scrollToSection(id: string, smooth: boolean) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - TOPBAR_OFFSET;
  window.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });
}

export function Settings({ params }: { params: string[] }) {
  const prefs = usePrefs();
  const [name, setName] = useState(prefs.displayName);
  const [dataVersion, setDataVersion] = useState(0);
  const auth = useAuth();
  const account = auth.phase === 'ready' ? auth.state : { mode: 'local' as const, email: undefined };
  const signOut = async () => {
    try { authStore.set(await api.post<AuthState>('/api/auth/logout')); } catch (e) { toast(e instanceof Error ? e.message : 'Algo deu errado.', 'alert'); }
  };
  const section = params[0];

  useEffect(() => { if (section) requestAnimationFrame(() => scrollToSection(section, false)); }, [section]);
  useEffect(() => { setName(prefs.displayName); }, [prefs.displayName]);

  const goTo = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    replaceHash(`#/configuracoes/${id}`);
    scrollToSection(id, true);
  };

  const saveName = () => {
    const v = name.trim().slice(0, 40);
    if (v === prefs.displayName) return;
    prefsStore.set({ displayName: v });
    toast('Nome salvo neste navegador.', 'check');
  };

  return (
    <div className="page">
      <PageHead title="Configurações" lead="Contas, provedores, limites e os dados que o Jarvis guarda sobre você." />
      <div className="settings">
        <nav className="settings-nav" aria-label="Seções">
          {SECTIONS.map(([id, l]) => <a key={id} href={`#/configuracoes/${id}`} onClick={(e) => goTo(e, id)}>{l}</a>)}
        </nav>
        <div className="settings-sections">

          <section id="perfil" className="card">
            <CardHead icon="user" title="Perfil" />
            <form className="form-grid" onSubmit={(e) => { e.preventDefault(); saveName(); }}>
              <div className="field"><label htmlFor="displayName">Como o Jarvis deve chamar você</label>
                <input className="input" id="displayName" maxLength={40} value={name} placeholder="Opcional"
                  onChange={(e) => setName(e.target.value)} onBlur={saveName} />
                <span className="hint">Salvo apenas neste navegador. Usado na saudação.</span></div>
              <div className="field"><label htmlFor="account">Conta</label>
                <input className="input" id="account" disabled value={account.mode === 'supabase' ? (account.email ?? 'Conectado') : 'Usuário local (sem login)'} />
                <span className="hint">{account.mode === 'supabase'
                  ? 'Entrada por código no e-mail + aplicativo autenticador.'
                  : 'Modo de desenvolvimento: sem login, e a API só aceita conexões desta máquina.'}</span></div>
            </form>
            {account.mode === 'supabase' && (
              <div className="card-foot"><button type="button" className="btn btn-sm" onClick={signOut}><Icon name="arrowLeft" />Sair desta conta</button></div>
            )}
            <div className="form-grid mt-10"><TimezoneField onSaved={() => setDataVersion((v) => v + 1)} /></div>
          </section>

          <section id="aparencia" className="card">
            <CardHead icon="sun" title="Aparência" />
            <RadioCards<Theme> name="theme" value={prefs.theme} onChange={(theme) => prefsStore.set({ theme })} options={[
              { value: 'system', label: 'Sistema', hint: 'Segue o claro/escuro do aparelho' },
              { value: 'dark', label: 'Console', hint: 'Meia-noite, o padrão do Jarvis' },
              { value: 'light', label: 'Diurno', hint: 'Instrumento sob luz do dia' }
            ]} />
          </section>

          <section id="voz" className="card">
            <CardHead icon="mic" title="Voz" />
            <p className="lead">A voz com que o Jarvis responde quando a opção responder falando está ligada. Ele começa a falar já na primeira frase.</p>
            <VoicePicker />
          </section>

          <section id="integracoes" className="card">
            <CardHead icon="plug" title="Contas e integrações" />
            <p className="lead">Cada conexão é pedida por você, com a menor permissão possível, e pode ser revogada aqui a qualquer momento.</p>
            <GmailIntegration />
            {INTEGRATIONS.filter((i) => i.name !== 'Gmail').map((i) => (
              <div key={i.name} className="integration">
                <div className="integration-icon"><Icon name={i.icon} /></div>
                <div className="row-main"><div className="row-title">{i.name}</div><div className="row-sub">{i.scope}</div></div>
                <span className="badge badge-off"><span className="dot off" />Não conectado</span>
                <button type="button" className="btn btn-sm" disabled title={stageLabel(i.stage)}>{stageLabel(i.stage)}</button>
              </div>
            ))}
          </section>

          <section id="provedores" className="card">
            <CardHead icon="brain" title="Provedores de IA" />
            <p className="lead">Chaves de API ficam como variáveis de ambiente do servidor, nunca no aplicativo.</p>
            <AiProviderStatus />
            <WebSearchStatus />
            {PROVIDERS.map((p) => (
              <div key={p.name} className="integration">
                <div className="integration-icon"><Icon name={p.icon} /></div>
                <div className="row-main"><div className="row-title">{p.name}</div><div className="row-sub">{p.note}</div></div>
                <span className="badge badge-off"><span className="dot off" />Não configurado</span>
              </div>
            ))}
          </section>

          <section id="limites" className="card">
            <CardHead icon="gauge" title="Limites e custos" />
            <p className="lead">Limites conferidos pelo servidor antes de cada chamada ao provedor pago. Ao atingir o limite, o Jarvis para e avisa você.</p>
            <LimitsLive />
          </section>

          <section id="memoria" className="card">
            <CardHead icon="brain" title="Memória" />
            <p className="lead">Tudo o que o Jarvis lembra sobre você está nesta lista, com origem e data. Ele usa estes itens em todas as conversas. Nada é memorizado sem aparecer aqui.</p>
            <MemoryManager />
          </section>

          <section id="privacidade" className="card">
            <CardHead icon="shield" title="Privacidade e dados" />
            <ul className="list">
              <li className="row"><div className="row-main"><div className="row-title">No banco de dados do Jarvis</div><div className="row-sub">Projetos, tarefas, rotina, fuso horário, memória, histórico da conversa, consumo de IA (só números) e um registro de atividade que guarda só a ação e a data, nunca o conteúdo.</div></div></li>
              <li className="row"><div className="row-main"><div className="row-title">Neste navegador</div><div className="row-sub">Apenas preferências: tema, nome de exibição e modo de estado.</div></div></li>
              <li className="row"><div className="row-main"><div className="row-title">Serviços de terceiros</div><div className="row-sub">Quando a IA está ligada, o que você escreve na conversa vai para a Anthropic junto com seus projetos, rotina e memória, para gerar a resposta. Fora isso, só as fontes tipográficas do Google Fonts são baixadas. Sem rastreamento ou analytics.</div></div></li>
            </ul>
            <h3 className="subhead">Atividade recente</h3>
            <ActivityList refreshKey={dataVersion} />
            <DataControls onChanged={() => setDataVersion((v) => v + 1)} />
            {account.mode === 'supabase' && <DeleteAccount />}
            <div className="card-foot">
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => { prefsStore.reset(); toast('Preferências locais restauradas.', 'refresh'); }}><Icon name="refresh" />Restaurar preferências deste navegador</button>
            </div>
          </section>

          <section id="laboratorio" className="card">
            <CardHead icon="flask" title="Laboratório de estados" />
            <p className="lead">Para avaliar a interface: escolha como todas as telas carregam seus dados. O mesmo controle fica no topo da página.</p>
            <RadioCards<StateMode> name="stateMode" value={prefs.stateMode} onChange={(stateMode) => prefsStore.set({ stateMode })} options={[
              { value: 'real', label: 'Dados reais', hint: 'Seus projetos e rotina; o resto aparece como não conectado' },
              { value: 'demo', label: 'Demonstração', hint: 'Dados fictícios marcados como DEMO' },
              { value: 'loading', label: 'Carregando', hint: 'Carregamento que não termina' },
              { value: 'error', label: 'Erro', hint: 'Falha simulada com nova tentativa' }
            ]} />
          </section>

          <section id="sobre" className="card">
            <CardHead icon="logo" title="Sobre" />
            <dl className="kv">
              <dt>Versão</dt><dd className="mono">{APP_VERSION}</dd>
              <dt>Etapa</dt><dd>4 · Gmail somente leitura, triagem e rascunhos (mais a 2: conversa, memória e limites)</dd>
              <dt>Login</dt><dd>{account.mode === 'supabase' ? 'Supabase: código por e-mail + autenticador' : 'Desativado (modo local de desenvolvimento)'}</dd>
              <dt>Conexões</dt><dd>Anthropic (IA) e Gmail somente leitura, quando configurados e conectados por você</dd>
              <dt>Documentação</dt><dd>Pasta <span className="mono">docs/</span> do projeto</dd>
            </dl>
          </section>
        </div>
      </div>
    </div>
  );
}
